import React, { useEffect, useState, useRef } from 'react';
import Cookies from 'js-cookie';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaCamera, 
  FaTrash, 
  FaSave, 
  FaTimes, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaSpinner,
  FaImage,
  FaUpload
} from 'react-icons/fa';

const Profile = ({ onSave }) => {
  const defaultProfilePic = 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
  
  const [profilePic, setProfilePic] = useState(defaultProfilePic);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [profileName, setProfileName] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [originalPic, setOriginalPic] = useState(defaultProfilePic);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [touched, setTouched] = useState(false);
  
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  
  const authToken = Cookies.get("authToken");
  const adminId = localStorage.getItem("adminId");

  // Warn user about unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // Track character count
  useEffect(() => {
    setCharCount(profileName.length);
  }, [profileName]);

  // Fetch profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        
        if (!authToken) {
          throw new Error("Authentication token not found");
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/apis/admin/getAdminProfile`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();

        if (data && data.data) {
          const adminData = data.data;
          const fetchedPic = adminData.image || defaultProfilePic;
          const fetchedName = adminData.collegeName || '';
          
          setProfilePic(fetchedPic);
          setOriginalPic(fetchedPic);
          setProfileName(fetchedName);
          setOriginalName(fetchedName);
          
          if (adminData._id && !adminId) {
            localStorage.setItem("adminId", adminData._id);
          }
        } else {
          throw new Error("Invalid data structure received");
        }
        
        setError(null);
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError("Failed to load profile: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [authToken, adminId]);

  // Handle image selection
  const handleChangePicture = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.match('image.*')) {
      setError('Please select an image file (JPEG, PNG, GIF)');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image size should be less than 2MB');
      return;
    }

    setImageLoading(true);
    setProfilePicFile(file);
    setHasChanges(true);
    setTouched(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setProfilePic(e.target.result);
      setImageLoading(false);
      setError(null);
    };
    reader.onerror = () => {
      setImageLoading(false);
      setError('Failed to load image');
    };
    reader.readAsDataURL(file);
    
    event.target.value = '';
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (!file) return;
    
    if (!file.type.match('image.*')) {
      setError('Please select an image file (JPEG, PNG, GIF)');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setError('Image size should be less than 2MB');
      return;
    }

    setImageLoading(true);
    setProfilePicFile(file);
    setHasChanges(true);
    setTouched(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target.result);
      setProfilePic(e.target.result);
      setImageLoading(false);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePicture = () => {
    if (window.confirm('Are you sure you want to remove your profile picture?')) {
      setProfilePic(defaultProfilePic);
      setProfilePicFile(null);
      setImagePreview(null);
      setHasChanges(true);
      setTouched(true);
      setError(null);
    }
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) { // Max 100 characters
      setProfileName(value);
      setHasChanges(value !== originalName || profilePicFile !== null || profilePic !== originalPic);
      setTouched(true);
      setError(null);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('Are you sure you want to discard your changes?')) {
        setProfileName(originalName);
        setProfilePic(originalPic);
        setProfilePicFile(null);
        setImagePreview(null);
        setHasChanges(false);
        setTouched(false);
        setError(null);
      }
    } else {
      setTouched(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      if (!profileName.trim()) {
        setError('College name is required');
        return;
      }

      if (!adminId) {
        setError('Admin ID not found. Please log in again.');
        return;
      }

      if (!authToken) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      setUpdating(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('collegeName', profileName);
      
      if (profilePicFile) {
        formData.append('image', profilePicFile);
      } else if (profilePic === defaultProfilePic && originalPic !== defaultProfilePic) {
        // Only send empty string if we're removing an existing image
        formData.append('image', '');
      }

      const url = `${process.env.REACT_APP_BACKEND_URL}/apis/admin/updateProfile/${adminId}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || "Failed to update profile");
      }

      // Update original values
      setOriginalName(profileName);
      setOriginalPic(profilePic);
      setSuccess('Profile updated successfully!');
      
      setProfilePicFile(null);
      setHasChanges(false);
      setTouched(false);
      
      if (onSave) {
        onSave(data.data.image, data.data.collegeName);
      }

      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile: " + err.message);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        <p className="mt-4 text-gray-500">Loading profile...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full"
      ref={formRef}
    >
      <div className="flex flex-col md:flex-row gap-8">
        {/* Profile Picture Section */}
        <div className="flex flex-col items-center md:w-1/3 space-y-4">
          <motion.div 
            className="relative group"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <div 
              className="relative"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <img
                src={profilePic}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg transition-all duration-200 group-hover:shadow-xl"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = defaultProfilePic;
                }}
              />
              
              {/* Image loading overlay */}
              <AnimatePresence>
                {imageLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center"
                  >
                    <FaSpinner className="w-8 h-8 text-white animate-spin" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Hover overlay for upload */}
              <motion.div 
                className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaCamera className="w-8 h-8 text-white" />
              </motion.div>
            </div>

            {/* Online status indicator */}
            <div className="absolute bottom-1 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
          </motion.div>
          
          <div className="flex flex-col space-y-2 w-full max-w-[200px]">
            <input
              type="file"
              ref={fileInputRef}
              accept="image/png, image/jpeg, image/jpg, image/gif"
              onChange={handleChangePicture}
              className="hidden"
            />
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => fileInputRef.current?.click()}
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors text-sm flex items-center justify-center gap-2"
            >
              <FaUpload className="w-4 h-4" />
              Change Photo
            </motion.button>
            
            {/* <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleRemovePicture}
              disabled={profilePic === defaultProfilePic}
              className={`text-red-600 px-4 py-2 rounded-lg border border-red-600 hover:bg-red-50 transition-colors text-sm flex items-center justify-center gap-2 ${
                profilePic === defaultProfilePic ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <FaTrash className="w-4 h-4" />
              Remove Photo
            </motion.button> */}

            <p className="text-xs text-gray-400 text-center mt-2">
              Drag & drop or click to upload
              <br />
              Max size: 2MB
            </p>
          </div>
        </div>

        {/* Profile Details Section */}
        <div className="flex-1 space-y-6 md:border-l md:pl-8 md:border-gray-200">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              College Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={profileName}
                onChange={handleNameChange}
                onBlur={() => setTouched(true)}
                className={`w-full px-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  touched && !profileName.trim() ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter college name"
                maxLength={100}
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <span className={`text-xs ${charCount > 90 ? 'text-orange-500' : 'text-gray-400'}`}>
                  {charCount}/100
                </span>
              </div>
            </div>
            {touched && !profileName.trim() && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-xs text-red-500 mt-1"
              >
                College name is required
              </motion.p>
            )}
          </div>

          {/* Status Messages */}
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-lg flex items-start gap-2"
              >
                <FaExclamationTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}
            
            {success && (
              <motion.div
                key="success"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="p-3 bg-green-50 border-l-4 border-green-500 text-green-700 rounded-lg flex items-start gap-2"
              >
                <FaCheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{success}</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleCancel}
              disabled={!hasChanges || updating}
              className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                !hasChanges || updating
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <FaTimes className="w-4 h-4" />
              Cancel
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSaveChanges}
              disabled={updating || !adminId || !hasChanges || !profileName.trim()}
              className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
                updating || !adminId || !hasChanges || !profileName.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              {updating ? (
                <>
                  <FaSpinner className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  Save Changes
                </>
              )}
            </motion.button>
          </div>

          {/* Unsaved changes indicator */}
          <AnimatePresence>
            {hasChanges && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg text-center"
              >
                You have unsaved changes
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Debug info - only in development */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs">
          <details>
            <summary className="font-medium text-gray-700 cursor-pointer">Debug Info</summary>
            <div className="mt-2 space-y-1 text-gray-600">
              <div>Admin ID: {adminId || 'Not found'}</div>
              <div>Auth Token: {authToken ? 'Present' : 'Missing'}</div>
              <div>Has Changes: {hasChanges ? 'Yes' : 'No'}</div>
              <div>Image File: {profilePicFile ? profilePicFile.name : 'None'}</div>
              <div>College Name Length: {charCount}/100</div>
            </div>
          </details>
        </div>
      )}
    </motion.div>
  );
};

export default Profile;